<template>
  <div class="add-card-page">
    <QHeader class="bg-primary text-white">
      <QToolbar>
        <QBtn flat round dense icon="icon-f-arrow-left" @click="goBack()" />
        <QToolbarTitle>
          {{ $t("ETAB.PAGES.PAYMENT.ADD_AND_PAY.@TITLE") }}
        </QToolbarTitle>
      </QToolbar>
    </QHeader>

    <QPageContainer>
      <QPage padding>
        <form @submit.prevent="onSubmit">
          <section>
            <IconLabel heavy :label="$t('ETAB.PAGES.PAYMENT.PAYMENT_CARD.@SUBTITLE')" icon="fad fa-credit-card" />
          </section>

          <section>
            <CardForm @reference-stripe-instances="referenceStripeInstances" />
          </section>

          <section v-if="shouldPay" class="text-right">
            <IosCheckbox v-model="shouldSaveCard" :label="$t('ETAB.PAGES.PAYMENT.ADD_AND_PAY.@SAVE_CARD')" :left-label="true" />
          </section>

          <QBtn
            class="full-width btn-navigation"
            color="secondary"
            :label="shouldPay ? $t('ETAB.PAGES.PAYMENT.ADD_AND_PAY.@PAY') : $t('ETAB.PAGES.PAYMENT.ADD_AND_PAY.@SAVE_CARD')"
            no-caps
            type="submit"
          />
        </form>
      </QPage>
    </QPageContainer>

    <QFooter class="bg-primary-ultra-light text-primary">
      <LogoDescription :logo="require('@etab/assets/img/logo_stripe.png')" :description="$t('ETAB.PAGES.PAYMENT.PAYMENT_CARD.@DESCRIPTION_STRIPE')" />
    </QFooter>
  </div>
</template>

<script>
  import { Keyboard } from "@capacitor/keyboard";
  import { StatusBar } from "@core/plugins/mobile";
  import { PAYMENT_RESPONSE, PAYMENT_TYPES, STRIPE_STATUS } from "@core/constants";
  import { PaymentService } from "@core/services";

  import { ETAB_ROUTES } from "@etab/config";
  import { paymentMixin } from "@etab/mixins";

  import CardForm from "@etab/components/payment/CardForm.component.vue";

  export default {
    components: {
      CardForm,
    },
    mixins: [paymentMixin],
    beforeRouteLeave(to, from, next) {
      if (this.isMobile) {
        Keyboard.hide();
      }
      next();
    },
    data() {
      return {
        stripeInstance: {},
        cardStripeElement: {},
        shouldSaveCard: true, // RG: Add Bank Card - we save the card by default
        shouldPay: false,
      };
    },
    created() {
      this.shouldPay = this.$route.name === ETAB_ROUTES.payment.addAndPay.name;
      this.demandId = this.$route.query.demandId;
      this.demandType = this.$route.query.demandType;
      this.extraId = this.$route.query.extraId;
      this.redirectTo = this.$route.query.redirectTo;
      this.upfront = this.$route.query.upfront || 0;
    },
    mounted() {
      StatusBar.setPrimaryBackground();
    },
    methods: {
      referenceStripeInstances({ stripeInstance, cardStripeElement }) {
        // we need those objects in order to get and use Stripe data
        this.stripeInstance = stripeInstance;
        this.cardStripeElement = cardStripeElement;
      },
      goBack() {
        // The method go(-1), does not work with 3d secure stripe modal on chrome browser
        this.$router.push(this.$router.options.history.state.back);
      },
      goToSuccessAfterPayment() {
        let nextRoute;
        if (this.redirectTo) {
          nextRoute = { name: this.redirectTo };
        } else {
          nextRoute = {
            name: ETAB_ROUTES.dashboard.recruitment.permanent.activation.success.name,
            params: { demandId: this.demandId, demandType: this.demandType },
            query: { extraId: this.extraId },
          };
        }
        // replace navigation in order to prevent coming back to this page
        this.$router.replace(nextRoute);
      },
      async onSubmit() {
        try {
          this.$_startGlobalLoading();
          if (this.shouldPay) {
            const demandId = this.demandId;
            const shouldSaveCard = this.shouldSaveCard;
            const upfront = this.upfront;

            await this.addCardAndPay({ demandId, shouldSaveCard, upfront });
            this.goToSuccessAfterPayment();
          } else {
            await this.addCard();
            this.goBack();
          }
        } catch (error) {
          this.$_handlePaymentError({ error });
          this.$_stopGlobalLoading();
          throw error;
        } finally {
          // we will stopLoading in the next page in case of success
        }
      },
      async addCard() {
        const paymentData = await this.getPaymentData();
        return PaymentService.addNewCard({ paymentId: paymentData });
      },
      async addCardAndPay({ demandId, shouldSaveCard, upfront, extraId }) {
        const type = shouldSaveCard ? PAYMENT_TYPES.SAVE_REQUIRED : PAYMENT_TYPES.NO_SAVE;
        const paymentToken = await this.getPaymentId();
        return PaymentService.handlePayment({ demandId, paymentToken, type, upfront, extraId });
      },
      async getPaymentId() {
        // check if Stripe instance is initialized
        if (this.stripeInstance && typeof this.stripeInstance.createPaymentMethod !== "function") {
          throw new Error("ERRORS.@SOMETHING_WENT_WRONG");
        }

        // https://stripe.com/docs/js/payment_methods/create_payment_method
        const { error, paymentMethod } = await this.stripeInstance.createPaymentMethod({
          type: "card",
          card: this.cardStripeElement,
        });
        await this.ensureDataCorrectness({ error });

        return paymentMethod.id;
      },
      async getPaymentData() {
        // check if Stripe instance is initialized
        if (this.stripeInstance && typeof this.stripeInstance.confirmCardSetup !== "function") {
          throw new Error("ERRORS.@SOMETHING_WENT_WRONG");
        }

        const cardToken = await PaymentService.initNewCardToken();

        if (cardToken && cardToken.status !== STRIPE_STATUS.REQUIRED_PAYMENT_METHOD) {
          throw new Error("ERRORS.@SOMETHING_WENT_WRONG");
        }

        // https://stripe.com/docs/js/setup_intents/confirm_card_setup
        const { error, setupIntent } = await this.stripeInstance.confirmCardSetup(cardToken.client_secret, {
          payment_method: {
            card: this.cardStripeElement,
          },
        });
        await this.ensureDataCorrectness({ error, setupIntent });

        return setupIntent.payment_method;
      },
      async ensureDataCorrectness({ error, setupIntent }) {
        if (error) {
          if (error.type && error.type !== PAYMENT_RESPONSE.VALIDATION_ERROR) {
            // the code appears valid for the front-end failchecks but the server refused it somehow
            // this is a valid error we might want to display
            throw error;
          }
          // this error will induce a silent fail
          error.name = PAYMENT_RESPONSE.INTEGRATION_ERROR;
          throw error;
        }

        if (setupIntent && setupIntent.status !== STRIPE_STATUS.SUCCESS) {
          throw new Error("ERRORS.@SOMETHING_WENT_WRONG");
        }
      },
    },
  };
</script>

<style lang="scss">
  .add-card-page {
    form {
      > section {
        margin-bottom: 30px;
      }
    }
  }
</style>
